import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useUser } from '../../contexts/UserContext';
import { apiService, API_ENDPOINTS } from '../../config/api';
import { getUnseenTours } from './feature-tours';
import SpotlightOverlay from './SpotlightOverlay';
import SpotlightTooltip from './SpotlightTooltip';
import SpotlightNotificationToast from './SpotlightNotificationToast';
import type { FeatureTour, FeaturesSeenMap, FeatureStatus } from '../../types';

const SETTLE_DELAY = 2000;
const INACTIVITY_TIMEOUT = 30000;
const ELEMENT_POLL_INTERVAL = 500;
const ELEMENT_POLL_TIMEOUT = 15000;

const isDev = process.env.NODE_ENV === 'development';
function debugLog(...args: unknown[]) {
  if (isDev) console.log('[Spotlight]', ...args);
}

interface SpotlightContextType {
  activeTour: FeatureTour | null;
  currentStepIndex: number;
  isVisible: boolean;
  nextStep: () => void;
  dismissTour: (status?: FeatureStatus) => void;

  showNotification: boolean;
  notificationTour: FeatureTour | null;
  startTour: (tour: FeatureTour) => void;
  skipTour: () => void;
  laterTour: () => void;
  cancelTour: () => void;
}

const defaultContext: SpotlightContextType = {
  activeTour: null,
  currentStepIndex: 0,
  isVisible: false,
  nextStep: () => {},
  dismissTour: () => {},
  showNotification: false,
  notificationTour: null,
  startTour: () => {},
  skipTour: () => {},
  laterTour: () => {},
  cancelTour: () => {},
};

const SpotlightContext = createContext<SpotlightContextType>(defaultContext);

export function useSpotlight(): SpotlightContextType {
  return useContext(SpotlightContext);
}

function isRouteMatch(pattern: string | undefined, currentPath: string): boolean {
  if (!pattern) return true;
  const patternParts = pattern.split('/');
  const pathParts = currentPath.split('/');
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every(
    (part, i) => part.startsWith(':') || part === pathParts[i]
  );
}

function findTargetElement(step: { targetSelector: string; targetFallback?: string }): Element | null {
  return (
    document.querySelector(step.targetSelector) ||
    (step.targetFallback ? document.querySelector(step.targetFallback) : null)
  );
}

/**
 * Waits for the step's target element to exist in the DOM and, if a readySelector is
 * provided, for that element to exist and NOT be disabled. This ensures data-heavy
 * pages (consent management, study dashboard) have finished loading before overlays appear.
 */
function waitForStepReady(
  step: { targetSelector: string; targetFallback?: string; readySelector?: string },
  timeout = ELEMENT_POLL_TIMEOUT
): Promise<Element | null> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const el =
        document.querySelector(step.targetSelector) ||
        (step.targetFallback ? document.querySelector(step.targetFallback) : null);

      if (el) {
        if (step.readySelector) {
          if (document.querySelector(step.readySelector)) {
            clearInterval(interval);
            resolve(el);
          }
        } else {
          clearInterval(interval);
          resolve(el);
        }
      }

      if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        resolve(null);
      }
    }, ELEMENT_POLL_INTERVAL);
  });
}

interface SpotlightProviderProps {
  children: React.ReactNode;
}

type InitState = 'idle' | 'loading' | 'done';
let globalInitState: InitState = 'idle';

if (isDev && (module as any).hot) {
  (module as any).hot.dispose(() => {
    globalInitState = 'idle';
  });
}

const SpotlightProvider: React.FC<SpotlightProviderProps> = ({ children }) => {
  const { isAuthenticated } = useSecureAuth();
  const { isApprovedResearcher, isMasterAccount, userProfile, loading: userLoading } = useUser();
  const location = useLocation();

  const [activeTour, setActiveTour] = useState<FeatureTour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const [featuresSeen, setFeaturesSeen] = useState<FeaturesSeenMap>({});
  const [notificationTour, setNotificationTour] = useState<FeatureTour | null>(null);
  const [showNotification, setShowNotification] = useState(false);

  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationRef = useRef(location.pathname);

  const isEligible =
    isAuthenticated && (isApprovedResearcher || isMasterAccount) && !userLoading;

  const persistFeatureStatus = useCallback(
    (tourId: string, status: FeatureStatus) => {
      const updated = { ...featuresSeen, [tourId]: { status, version: 1, seenAt: new Date().toISOString() } };
      setFeaturesSeen(updated);
      apiService.post(API_ENDPOINTS.FEATURES.SEEN, { featureId: tourId, status }).catch(() => {});
    },
    [featuresSeen]
  );

  const dismissTour = useCallback(
    (status: FeatureStatus = 'seen') => {
      if (!activeTour) return;

      const tourId = activeTour.id;

      debugLog('Dismissing tour:', tourId, { status });

      setIsVisible(false);
      setActiveTour(null);
      setCurrentStepIndex(0);
      persistFeatureStatus(tourId, status);

      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = null;
      }
    },
    [activeTour, persistFeatureStatus]
  );

  const startTour = useCallback(
    (tour: FeatureTour) => {
      setShowNotification(false);
      setNotificationTour(null);

      const step = tour.steps[0];
      waitForStepReady(step).then((el) => {
        if (el) {
          setActiveTour(tour);
          setCurrentStepIndex(0);
          setIsVisible(true);
        } else {
          debugLog('Target element never appeared for tour start — skipping');
          persistFeatureStatus(tour.id, 'cancelled');
        }
      });
    },
    [persistFeatureStatus]
  );

  const nextStep = useCallback(() => {
    if (!activeTour) return;

    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= activeTour.steps.length) {
      dismissTour('seen');
      return;
    }

    const nextStepConfig = activeTour.steps[nextIndex];
    const routeMatch = isRouteMatch(nextStepConfig.route, location.pathname);

    setCurrentStepIndex(nextIndex);

    if (routeMatch) {
      setIsVisible(false);
      waitForStepReady(nextStepConfig).then((el) => {
        if (el) {
          setIsVisible(true);
        } else {
          debugLog('Step element never appeared, dismissing tour');
          dismissTour('cancelled');
        }
      });
    } else {
      setIsVisible(false);
      waitForStepReady(nextStepConfig).then((el) => {
        if (el) {
          setIsVisible(true);
        } else {
          debugLog('Step element never appeared after route change, dismissing tour');
          dismissTour('cancelled');
        }
      });
    }
  }, [activeTour, currentStepIndex, location.pathname, dismissTour]);

  const skipTour = useCallback(() => {
    if (!notificationTour) return;
    persistFeatureStatus(notificationTour.id, 'skipped');
    setShowNotification(false);
    setNotificationTour(null);
  }, [notificationTour, persistFeatureStatus]);

  const laterTour = useCallback(() => {
    if (!notificationTour) return;
    persistFeatureStatus(notificationTour.id, 'later');
    setShowNotification(false);
    setNotificationTour(null);
  }, [notificationTour, persistFeatureStatus]);

  const cancelTour = useCallback(() => {
    dismissTour('cancelled');
  }, [dismissTour]);

  // Dismiss on Escape key
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismissTour('cancelled');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, dismissTour]);

  // Handle route changes for multi-page tours
  useEffect(() => {
    locationRef.current = location.pathname;

    if (!activeTour || !isVisible) return;

    const currentStep = activeTour.steps[currentStepIndex];
    if (!currentStep) return;

    const matchesNewRoute = isRouteMatch(currentStep.route, location.pathname);
    if (!matchesNewRoute) {
      const stepOnThisRoute = activeTour.steps.find((s) =>
        isRouteMatch(s.route, location.pathname)
      );
      if (stepOnThisRoute) {
        const newIndex = activeTour.steps.indexOf(stepOnThisRoute);
        setCurrentStepIndex(newIndex);
        setIsVisible(false);
        waitForStepReady(stepOnThisRoute).then((el) => {
          if (el) setIsVisible(true);
          else dismissTour('cancelled');
        });
      }
    }
  }, [location.pathname, activeTour, isVisible, currentStepIndex, dismissTour]);

  // Inactivity auto-dismiss (30s)
  useEffect(() => {
    if (!isVisible) return;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => dismissTour('cancelled'), INACTIVITY_TIMEOUT);
    };

    resetTimer();

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((evt) => document.addEventListener(evt, resetTimer, { passive: true }));

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach((evt) => document.removeEventListener(evt, resetTimer));
    };
  }, [isVisible, dismissTour]);

  // Re-check target element visibility after route changes for the current step
  useEffect(() => {
    if (!isVisible || !activeTour) return;

    const currentStep = activeTour.steps[currentStepIndex];
    if (!currentStep) return;

    if (!isRouteMatch(currentStep.route, location.pathname)) return;

    if (!findTargetElement(currentStep)) {
      setIsVisible(false);
      waitForStepReady(currentStep).then((found) => {
        if (found) {
          setIsVisible(true);
        } else {
          debugLog('Step element lost and never returned, dismissing');
          dismissTour('cancelled');
        }
      });
    }
  }, [location.pathname, isVisible, activeTour, currentStepIndex, dismissTour]);

  // Main initialization
  useEffect(() => {
    if (!isEligible || userLoading) {
      debugLog('Not eligible yet:', { isAuthenticated, isApprovedResearcher, isMasterAccount, userLoading });
      return;
    }

    if (globalInitState !== 'idle') {
      debugLog('Already initialized or loading, skipping. State:', globalInitState);
      return;
    }

    globalInitState = 'loading';
    debugLog('Starting initialization...');

    let cancelled = false;
    let settleTimeout: ReturnType<typeof setTimeout> | null = null;

    const init = async () => {
      let merged: FeaturesSeenMap = {};

      try {
        const response = await apiService.get(API_ENDPOINTS.FEATURES.SEEN);
        if (cancelled) return;
        if (response?.featuresSeen) {
          merged = response.featuresSeen;
          setFeaturesSeen(merged);
          debugLog('API features seen:', merged);
        }
      } catch (err) {
        debugLog('API fetch failed — tours not available this session:', err);
      }

      if (cancelled) return;

      const role = userProfile?.role || 'researcher';
      const registeredAt = userProfile?.approvedAt;
      const unseen = getUnseenTours(merged, role, registeredAt);

      debugLog('Tour selection:', {
        role,
        registeredAt,
        unseenCount: unseen.length,
        unseenIds: unseen.map((t) => t.id),
      });

      if (unseen.length === 0) {
        debugLog('No unseen tours — nothing to show');
        globalInitState = 'done';
        return;
      }

      const tour = unseen[0];

      if (tour.notification) {
        if (tour.notificationRoute && !isRouteMatch(tour.notificationRoute, location.pathname)) {
          debugLog(`Tour "${tour.id}" has notificationRoute but current path doesn't match — will retry`);
          cancelled = true;
          globalInitState = 'idle';
          // Clear the settle timeout to avoid firing after route change
          return;
        }
        debugLog(`Showing notification for tour: "${tour.id}"`);
        setNotificationTour(tour);
        setShowNotification(true);
        globalInitState = 'done';
      } else {
        debugLog(`Selected tour: "${tour.id}". Waiting ${SETTLE_DELAY}ms for dashboard to settle...`);
        settleTimeout = setTimeout(() => {
          if (cancelled) return;

          debugLog('Waiting for first step element to be ready...');
          waitForStepReady(tour.steps[0]).then((targetEl) => {
            if (cancelled) return;

            if (!targetEl) {
              debugLog('Target element never appeared — skipping tour');
              globalInitState = 'done';
              return;
            }

            debugLog('Showing tour!');
            setActiveTour(tour);
            setCurrentStepIndex(0);
            setIsVisible(true);
            globalInitState = 'done';
          });
        }, SETTLE_DELAY);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (settleTimeout) clearTimeout(settleTimeout);
      if (globalInitState === 'loading') {
        globalInitState = 'idle';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEligible, userLoading, userProfile?.role, userProfile?.approvedAt, location.pathname]);

  const contextValue = useMemo(
    () => ({
      activeTour,
      currentStepIndex,
      isVisible,
      nextStep,
      dismissTour: () => dismissTour('seen'),
      showNotification,
      notificationTour,
      startTour,
      skipTour,
      laterTour,
      cancelTour,
    }),
    [
      activeTour,
      currentStepIndex,
      isVisible,
      nextStep,
      dismissTour,
      showNotification,
      notificationTour,
      startTour,
      skipTour,
      laterTour,
      cancelTour,
    ]
  );

  const currentStep = activeTour?.steps[currentStepIndex];

  return (
    <SpotlightContext.Provider value={contextValue}>
      {children}
      {showNotification && notificationTour && (
        createPortal(
          <SpotlightNotificationToast
            tour={notificationTour}
            onTakeTour={() => startTour(notificationTour)}
            onSkip={skipTour}
            onLater={laterTour}
            onDismiss={() => {
              setShowNotification(false);
              setNotificationTour(null);
            }}
          />,
          document.body
        )
      )}
      {isVisible && activeTour && currentStep &&
        createPortal(
          <>
            <SpotlightOverlay
              step={currentStep}
              onClickOutside={() => dismissTour('cancelled')}
            />
            <SpotlightTooltip
              step={currentStep}
              stepIndex={currentStepIndex}
              totalSteps={activeTour.steps.length}
              onNext={nextStep}
              onDismiss={() => dismissTour('cancelled')}
            />
          </>,
          document.body
        )}
    </SpotlightContext.Provider>
  );
};

export default SpotlightProvider;
